// frontend/src/components/NotificationContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

// Create context
const NotificationContext = createContext();

// Custom hook to use notifications
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Toast component
const Toast = ({ id, message, type, duration, onRemove }) => {
  const [isVisible, setIsVisible] = useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onRemove(id), 300); // Wait for fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onRemove]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} className="text-green-400" />;
      case 'error':
        return <XCircle size={20} className="text-red-400" />;
      case 'warning':
        return <AlertCircle size={20} className="text-yellow-400" />;
      case 'info':
      default:
        return <Info size={20} className="text-blue-400" />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-900 border-green-500';
      case 'error':
        return 'bg-red-900 border-red-500';
      case 'warning':
        return 'bg-yellow-900 border-yellow-500';
      case 'info':
      default:
        return 'bg-blue-900 border-blue-500';
    }
  };

  return (
    <div
      className={`fixed top-4 right-4 z-50 max-w-sm w-full transition-all duration-300 transform ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div className={`${getBackgroundColor()} border rounded-lg p-4 shadow-lg`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-white whitespace-pre-wrap">
              {message}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(() => onRemove(id), 300);
              }}
              className="text-gray-400 hover:text-white focus:outline-none focus:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast container component
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{ top: `${index * 80}px` }}
          className="relative"
        >
          <Toast
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onRemove={removeToast}
          />
        </div>
      ))}
    </div>
  );
};

// Notification provider component
export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Add a new toast
  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      type,
      duration
    };

    setToasts(prev => [...prev, toast]);

    // Auto-remove after duration (backup in case component unmounts)
    setTimeout(() => {
      removeToast(id);
    }, duration + 500);
  }, []);

  // Remove a specific toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Remove all toasts
  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Success toast shortcut
  const addSuccessToast = useCallback((message, duration = 4000) => {
    addToast(message, 'success', duration);
  }, [addToast]);

  // Error toast shortcut
  const addErrorToast = useCallback((message, duration = 6000) => {
    addToast(message, 'error', duration);
  }, [addToast]);

  // Warning toast shortcut
  const addWarningToast = useCallback((message, duration = 5000) => {
    addToast(message, 'warning', duration);
  }, [addToast]);

  // Info toast shortcut
  const addInfoToast = useCallback((message, duration = 4000) => {
    addToast(message, 'info', duration);
  }, [addToast]);

  const value = {
    // Main methods
    addToast,
    removeToast,
    clearToasts,
    
    // Convenience methods
    addSuccessToast,
    addErrorToast,
    addWarningToast,
    addInfoToast,
    
    // State
    toasts
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </NotificationContext.Provider>
  );
};